import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import FilterSection from "../../../organisms/Sections/filter-controller-section/FilterSection";
import FullWidthCard, {
  CardContentItem,
} from "../../../organisms/Cards/full-width-card/FullWidthCard";
import HelperLoader from "../../../molecules/HelperLoader/HelperLoader";
import BreadCrumb from "../../../organisms/Breadcrumb/BreadCrumb";
import Headline from "../../../organisms/Headline/Headline";
import Loader from "../../../atoms/Loader/Loader";
import Button from "../../../atoms/Buttons/Button/Button";
import Pagination from "../../../molecules/Pagination/Pagination";

import { AppDispatch, RootState } from "../../../../slices/store";
import UseAuthConfig from "../../../../hooks/useAuthConfig";
import { getWarmLeads } from "../../../../slices/leads/leadManagementSlice";
import { handleDeleteCardService } from "../../../../pages/leads-management/service/LeadService";
import { TodosContext } from "../../../../context";
import filterContentLeads from "../../../../utils/helpers/helperDataset/lead-management-contents/filterContentLeads.json";
import cardContent from "../../../../utils/helpers/helperDataset/lead-management-contents/cardContentLeads.json";
import useUserRole from "../../../../hooks/useUserRole";
import useDownloadLeads from "../../../../hooks/useDownloadLeads";
import { UserRole } from "../../../../utils";
import { LEAD_IDENTIFIERS } from "../../../../pages/leads-management/models/ILead";
import useDebouncedDispatch from "../../../../utils/DebouncedDispatch";
import LeadStatusDefinitions from "../../../organisms/LeadStatusDefinitions/LeadStatusDefinitions";
import { getLeadRatingText } from "../../../../utils/getLeadRatingText";

const STORAGE_KEY = "warmLeadsActivePage";

const WarmLeadTemplate = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { addAlert } = useContext(TodosContext);
  const { protectedResources }: any = UseAuthConfig();
  const userRole = useUserRole();
  const debouncedDispatch = useDebouncedDispatch(dispatch);

  const leadDetails = useSelector(
    (state: RootState) => state.leadsManagement.warmLeads
  );
  const status = useSelector(
    (state: RootState) => state.leadsManagement.status
  );

  const {
    contentOne,
    contentTwo,
    contentOneShow,
    contentTwoShow,
    breadcrumbOne,
    breadcrumbTwo,
  } = filterContentLeads;

  const savedPage = Number(localStorage.getItem(STORAGE_KEY)) || 1;

  const [isLoading, setIsLoading] = useState(false);
  const [activePage, setActivePage] = useState(savedPage);
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const [reqRequests, setReqRequest] = useState({
    PageNumber: savedPage,
    SearchText: "",
    SortOrder: 1,
    PageSize: 10,
    OrderBy: "",
    ParameterOne: "Requested,Rejected",
    ParameterTwo: "",
    ParameterThree: "Requested,Rejected",
    url: protectedResources.apiTodoList.endpoint,
    accessToken: localStorage.getItem("AccessToken") || "",
    leadType: "MediumIntent",
  });

  const { handleConfirm, handleSelectedConfirm, isLoadingDownloads } =
    useDownloadLeads(protectedResources);

  const selectedCount = selectedLeadIds.length;
  const hasLeads = !!leadDetails?.data?.length;
  const totalRecords = leadDetails?.totalRecords ?? 0;
  const totalPages = leadDetails?.totalPages ?? 0;

  const toggleLeadSelection = useCallback((leadId: number) => {
    setSelectedLeadIds((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  }, []);

  const isLeadSelected = useCallback(
    (leadId: number) => selectedLeadIds.includes(leadId),
    [selectedLeadIds]
  );

  const handleSetFilteredValue = useCallback((inputValue: string) => {
    setIsLoading(true);
    setActivePage(1);

    setReqRequest((prevState) => ({
      ...prevState,
      SearchText: inputValue || "",
      PageNumber: 1,
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setIsLoading(true);
    setActivePage(page);

    setReqRequest((prevState) => ({
      ...prevState,
      PageNumber: page,
    }));
  }, []);

  const handleDeleteCard = useCallback(
    (data: any) => {
      handleDeleteCardService(
        data,
        reqRequests,
        setIsLoading,
        dispatch,
        addAlert,
        false,
        navigate,
        getWarmLeads,
        protectedResources
      );
    },
    [reqRequests, dispatch, addAlert, navigate, protectedResources]
  );

  useEffect(() => {
    setIsLoading(true);
    dispatch(getWarmLeads(reqRequests));
  }, [dispatch]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, activePage.toString());
  }, [activePage]);

  useEffect(() => {
    setIsLoading(true);

    if (reqRequests.SearchText) {
      debouncedDispatch(getWarmLeads, reqRequests);

      return () => {
        debouncedDispatch.cancel();
      };
    }

    dispatch(getWarmLeads(reqRequests));
  }, [reqRequests, dispatch, debouncedDispatch]);

  useEffect(() => {
    setIsLoading(false);
  }, [leadDetails, isLoadingDownloads]);

  const leadCards = useMemo(
    () =>
      leadDetails?.data?.map((user: any) => {
        const updatedLead = {
          ...user,
          rating: getLeadRatingText(user.rating),
        };

        return (
          <FullWidthCard
            cardContent={cardContent as CardContentItem[]}
            key={user.id}
            data={updatedLead}
            deleteCard={handleDeleteCard}
            isSelected={isLeadSelected(user.id)}
            onSelect={() => toggleLeadSelection(user.id)}
            isLeads={true}
            canEdit={false}
          />
        );
      }),
    [leadDetails?.data, handleDeleteCard, isLeadSelected, toggleLeadSelection]
  );

  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />

      <Headline
        name="Medium Intent Leads"
        subHeading="Smarter lead management for better conversions"
      />

      <div className="content-wrapper">
        <div className="filter-wrapper">
          <FilterSection
            contentOne={contentOne}
            contentTwo={contentTwo}
            breadcrumbOne={breadcrumbOne}
            breadcrumbTwo={breadcrumbTwo}
            handleInputText={handleSetFilteredValue}
            contentOneShow={contentOneShow}
            contentTwoShow={contentTwoShow}
          />

          <div className="download-btn-section d-flex align-items-center gap-3">
            {selectedCount > 0 && (
              <div className="clear-btn">
                <Button
                  cssClass="btn-secondary"
                  text="Clear Selection"
                  onClick={() => setSelectedLeadIds([])}
                />
              </div>
            )}

            {userRole !== UserRole.Operator &&
              (leadDetails?.data?.length ?? 0) > 0 && (
                <Button
                  cssClass="btn-primary"
                  text={
                    selectedCount > 0
                      ? `Export (${selectedCount})`
                      : "Export All"
                  }
                  icon="download"
                  onClick={() => {
                    if (selectedLeadIds.length > 0) {
                      handleSelectedConfirm(selectedLeadIds);
                    } else {
                      handleConfirm(LEAD_IDENTIFIERS.WARM_LEADS);
                    }
                  }}
                  activeColor="#ffffff"
                />
              )}
          </div>
        </div>
      </div>

      <div className="content-wrapper">
        {hasLeads ? (
          leadCards
        ) : (
          <HelperLoader status={status} totalRecords={totalRecords} />
        )}

        <div className="pagination-section">
          <Pagination
            pageCount={totalPages}
            pageNumber={activePage}
            onPageChange={handlePageChange}
            totalPages={totalPages}
            pageSize={reqRequests.PageSize}
            totalRecords={totalRecords}
            label="Medium Intent leads"
          />
        </div>
      </div>

      <LeadStatusDefinitions />

      {(isLoading || isLoadingDownloads) && <Loader />}
    </Container>
  );
};

export default WarmLeadTemplate;
